// 媒体の表示設定。端末ローカル保存（localStorage・サーバー非同期）。
//
// 「今回のスコープはsystem側、後にユーザーCUSTOM」の CUSTOM 側の第一歩。
// サーバーの news_sources がシステム側の購読先マスタで、
// そのうちどれを自分のフィードに出すかをこの端末だけの好みとして持つ。
//
// **保存するのは「既定からの差分」だけ。** 有効な slug の一覧でも、
// 除外した slug の一覧でもない。
// 媒体ごとにシステム側の既定 (news_sources.default_enabled) があり、
// ユーザーが明示的に切り替えたものだけをここに記録する。
//
// この形にしている理由:
//   - システムに媒体を追加したとき、既存ユーザーにも既定通りに出る
//   - システム側で既定を変えたとき（例: インサイドを既定OFFに）、
//     自分で触っていない人には反映され、触った人の選択は尊重される
//   - 「既定に戻す」が差分を捨てるだけで実装できる
import type { NewsSource } from '../types/News';

const KEY = 'news_source_prefs_v1';
// 旧形式（除外slugの配列）。読み込み時だけ変換して引き継ぐ
const LEGACY_KEY = 'news_muted_sources_v1';

/** slug -> ユーザーが明示的に選んだ値。既定のままの媒体はキーごと存在しない */
export type SourceOverrides = Record<string, boolean>;

export const readOverrides = (): SourceOverrides => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as SourceOverrides;
      }
      return {};
    }
    // 旧形式からの移行。除外していた媒体を「明示的にOFF」として引き継ぐ
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const muted = JSON.parse(legacy);
      if (Array.isArray(muted)) {
        const migrated: SourceOverrides = {};
        muted.forEach((slug: unknown) => { migrated[String(slug)] = false; });
        write(migrated);
        localStorage.removeItem(LEGACY_KEY);
        return migrated;
      }
    }
    return {};
  } catch {
    return {};
  }
};

const write = (overrides: SourceOverrides): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(overrides));
  } catch {
    /* localStorage 利用不可時は黙って無視 */
  }
};

export const isSourceEnabled = (
  source: NewsSource,
  overrides: SourceOverrides
): boolean => overrides[source.slug] ?? source.default_enabled;

/**
 * 1媒体の表示/非表示を切り替える。
 * 既定と同じ値に戻したときは差分ごと消す（＝以後は既定に追従する）。
 */
export const setSourceEnabled = (
  source: NewsSource,
  enabled: boolean,
  overrides: SourceOverrides
): SourceOverrides => {
  const next = { ...overrides };
  if (enabled === source.default_enabled) {
    delete next[source.slug];
  } else {
    next[source.slug] = enabled;
  }
  write(next);
  return next;
};

/** 差分を全部捨てて既定に戻す */
export const resetToDefaults = (): SourceOverrides => {
  write({});
  return {};
};

/** 全媒体を表示する（デフォルトOFFのものも明示的にONにする） */
export const enableAllSources = (sources: NewsSource[]): SourceOverrides => {
  const next: SourceOverrides = {};
  sources.forEach((s) => {
    if (!s.default_enabled) next[s.slug] = true;
  });
  write(next);
  return next;
};

/** 全媒体を非表示にする（デフォルトONのものも明示的にOFFにする） */
export const disableAllSources = (sources: NewsSource[]): SourceOverrides => {
  const next: SourceOverrides = {};
  sources.forEach((s) => {
    if (s.default_enabled) next[s.slug] = false;
  });
  write(next);
  return next;
};

/** ユーザーが何か触っているか。触っていなければ「既定に戻す」は無効でよい */
export const hasOverrides = (overrides: SourceOverrides): boolean =>
  Object.keys(overrides).length > 0;

/**
 * APIへ渡す media フィルタを組み立てる。
 *
 * 全媒体が有効なときだけ空配列を返す（＝APIにフィルタを渡さない）。
 * 全 slug を並べても結果は同じだが、URLが伸びる上に
 * サーバー側のキャッシュキーが媒体構成の変更で毎回割れる。
 *
 * **1つも有効でないときも空配列になる点に注意。** 空配列は
 * 「フィルタなし＝全件」の意味なので、そのまま投げると全媒体が出てしまう。
 * 呼び出し側は「1つも有効でない」を先に判定して、取得自体を止めること
 * （NewsPage の allMuted）。
 */
export const toSourceFilter = (
  sources: NewsSource[],
  overrides: SourceOverrides
): string[] => {
  const enabled = sources.filter((s) => isSourceEnabled(s, overrides));
  if (enabled.length === sources.length) return [];
  return enabled.map((s) => s.slug);
};
